/**
 * Poll `tick` every `intervalMs` until it returns true, `stop()` is called, or `timeoutMs` passes
 * (then `onTimeout` runs once). Ticks never overlap; a tick that throws just tries again next time.
 * Returns stop(). Used by the unsubscribe modals to watch a browser task (B-20).
 */
export function pollUntil(
  tick: () => Promise<boolean>,
  opts: { intervalMs: number; timeoutMs: number; onTimeout: () => void },
): () => void {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = Date.now() + opts.timeoutMs;

  const run = async () => {
    if (stopped) return;
    if (Date.now() >= deadline) {
      stopped = true;
      opts.onTimeout();
      return;
    }
    let done = false;
    try { done = await tick(); } catch { /* keep polling */ }
    if (stopped) return;
    if (done) { stopped = true; return; }
    timer = setTimeout(run, opts.intervalMs);
  };
  run();

  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
  };
}
